import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function DocumentPage() {
  const [formData, setFormData] = useState({
    company: '',
    department: '',
    position: '',
    lastName: '',
    firstName: '',
    email: '',
    phone: '',
    usage: '',
    privacy: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const submitData = new URLSearchParams();
    submitData.append('company', formData.company);
    submitData.append('department', formData.department);
    submitData.append('position', formData.position);
    submitData.append('lastName', formData.lastName);
    submitData.append('firstName', formData.firstName);
    submitData.append('email', formData.email);
    submitData.append('phone', formData.phone);
    submitData.append('usage', formData.usage);
    submitData.append('privacy', formData.privacy ? 'yes' : 'no');

    try {
      const response = await fetch('https://readdy.ai/api/form/d62c97gr3m6fkobknqdg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          company: '',
          department: '',
          position: '',
          lastName: '',
          firstName: '',
          email: '',
          phone: '',
          usage: '',
          privacy: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="py-6 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="cursor-pointer">
            <img 
              src="https://static.readdy.ai/image/e6428b60f9e010c4d343b75238d76ec4/b80d6f63db625f354fc913d075307be3.png" 
              alt="ジョゼロD" 
              className="h-8"
            />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          {/* Title */}
          <h1 className="text-3xl font-bold text-center text-gray-900 mb-10">
            資料をダウンロード
          </h1>

          {/* Form Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm">
            <p className="text-right text-sm text-gray-500 mb-6">
              <span className="text-red-500">*</span> は必須項目です
            </p>

            {submitStatus === 'success' ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-green-600"></i>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">送信完了</h2>
                <p className="text-gray-600 mb-6">
                  ダウンロードURLを記載したメールをお送りいたしました。
                </p>
                <Link 
                  to="/" 
                  className="inline-flex items-center text-cyan-500 hover:text-cyan-600 cursor-pointer"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  サイトトップへ
                </Link>
              </div>
            ) : (
              <form 
                id="document-download-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                {/* 会社名 */}
                <div className="flex items-start">
                  <label className="w-40 pt-2 text-gray-700 flex-shrink-0">
                    <span className="text-red-500 mr-1">*</span>
                    会社名
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="例) 株式会社カオナビ"
                    required
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
                  />
                </div>

                {/* 部署区分 */}
                <div className="flex items-start">
                  <label className="w-40 pt-2 text-gray-700 flex-shrink-0">
                    <span className="text-red-500 mr-1">*</span>
                    部署区分
                  </label>
                  <select
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    required
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm cursor-pointer"
                  >
                    <option value="">(部署区分を選択してください)</option>
                    <option value="人事・総務部門">人事・総務部門</option>
                    <option value="経理・財務部門">経理・財務部門</option>
                    <option value="経営企画部門">経営企画部門</option>
                    <option value="情報システム部門">情報システム部門</option>
                    <option value="マーケティング・広報部門">マーケティング・広報部門</option>
                    <option value="営業・販売部門">営業・販売部門</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                {/* 役職 */}
                <div className="flex items-start">
                  <label className="w-40 pt-2 text-gray-700 flex-shrink-0">
                    <span className="text-red-500 mr-1">*</span>
                    役職
                  </label>
                  <select
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    required
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm cursor-pointer"
                  >
                    <option value="">(役職を選択してください)</option>
                    <option value="経営者・役員 相当">経営者・役員 相当</option>
                    <option value="部長 相当">部長 相当</option>
                    <option value="課長 相当">課長 相当</option>
                    <option value="係長・主任 相当">係長・主任 相当</option>
                    <option value="一般社員 相当">一般社員 相当</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                {/* 氏名 */}
                <div className="flex items-start">
                  <label className="w-40 pt-2 text-gray-700 flex-shrink-0">
                    <span className="text-red-500 mr-1">*</span>
                    氏名:姓
                  </label>
                  <div className="flex-1 flex gap-4">
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="例) 顔那比"
                      required
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
                    />
                    <span className="pt-2 text-gray-700">名</span>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="例) 太郎"
                      required
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                {/* メールアドレス */}
                <div className="flex items-start">
                  <label className="w-40 pt-2 text-gray-700 flex-shrink-0">
                    <span className="text-red-500 mr-1">*</span>
                    メールアドレス
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="例) taro.kaonavi@kaonavi.jp"
                    required
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
                  />
                </div>

                {/* 電話番号 */}
                <div className="flex items-start">
                  <label className="w-40 pt-2 text-gray-700 flex-shrink-0">
                    <span className="text-red-500 mr-1">*</span>
                    電話番号
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="例) 080-1234-5678"
                    required
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
                  />
                </div>

                {/* カオナビご利用状況 */}
                <div className="flex items-start">
                  <label className="w-40 pt-2 text-gray-700 flex-shrink-0">
                    <span className="text-red-500 mr-1">*</span>
                    カオナビご利用状況
                  </label>
                  <div className="flex-1">
                    <div className="flex gap-6">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="usage"
                          value="未導入"
                          checked={formData.usage === '未導入'}
                          onChange={handleChange}
                          required
                          className="w-4 h-4 text-cyan-500 border-gray-300 focus:ring-cyan-500 cursor-pointer"
                        />
                        <span className="ml-2 text-gray-700">未導入</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="usage"
                          value="不明"
                          checked={formData.usage === '不明'}
                          onChange={handleChange}
                          className="w-4 h-4 text-cyan-500 border-gray-300 focus:ring-cyan-500 cursor-pointer"
                        />
                        <span className="ml-2 text-gray-700">不明</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="usage"
                          value="導入済み"
                          checked={formData.usage === '導入済み'}
                          onChange={handleChange}
                          className="w-4 h-4 text-cyan-500 border-gray-300 focus:ring-cyan-500 cursor-pointer"
                        />
                        <span className="ml-2 text-gray-700">導入済み</span>
                      </label>
                    </div>
                    <p className="text-sm text-gray-500 mt-3">
                      ※お客様への紹介をご検討、またはパートナー制度に興味をお持ちの方は
                      <a href="#" className="text-cyan-500 hover:underline cursor-pointer">セールスパートナー制度のフォーム</a>へ
                    </p>
                  </div>
                </div>

                {/* 個人情報の取り扱い */}
                <div className="flex items-start">
                  <label className="w-40 pt-2 text-gray-700 flex-shrink-0">
                    <span className="text-red-500 mr-1">*</span>
                    個人情報の取り扱い
                  </label>
                  <div className="flex-1">
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        name="privacy"
                        checked={formData.privacy}
                        onChange={handleChange}
                        required
                        className="w-4 h-4 mt-1 text-cyan-500 border-gray-300 rounded focus:ring-cyan-500 cursor-pointer"
                      />
                      <span className="ml-2 text-gray-700">個人情報の取り扱いについて同意します。</span>
                    </label>
                    <a 
                      href="https://corp.kaonavi.jp/privacy.html" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-cyan-500 hover:underline mt-3 text-sm cursor-pointer"
                    >
                      個人情報の取り扱い
                      <i className="ri-external-link-line ml-1"></i>
                    </a>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-cyan-500 text-white font-semibold rounded-lg hover:bg-cyan-600 transition-colors flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        送信中...
                      </>
                    ) : (
                      <>
                        入力内容を送信する
                        <i className="ri-arrow-right-line"></i>
                      </>
                    )}
                  </button>
                </div>

                <p className="text-center text-sm text-gray-500">
                  フォーム送信後、ダウンロードURLを記載したメールをお送り致します。
                </p>

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <p className="text-red-600">送信に失敗しました。もう一度お試しください。</p>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-gray-600 mb-4">
            上記のフォームから送信できない場合は、<strong>info@kaonavi.jp</strong>までメールをお送りください。
          </p>
          <p className="text-gray-600 mb-4">お電話でのお問い合わせも承っております。</p>
          <a href="tel:03-6633-2781" className="text-2xl font-bold text-cyan-500 flex items-center justify-center gap-2 mb-2 cursor-pointer">
            <i className="ri-phone-line"></i>
            03-6633-2781
          </a>
          <p className="text-sm text-gray-500 mb-8">10:00-18:00（土日祝除く）</p>
          
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-orange-500 cursor-pointer">
            <i className="ri-arrow-left-line mr-2"></i>
            サイトトップへ
          </Link>

          <div className="flex items-center justify-center gap-6 mt-8">
            <img src="https://www.kaonavi.jp/img/common/img_iso27001.png" alt="ISO27001" className="h-12" />
            <img src="https://www.kaonavi.jp/img/common/img_iso27017.png" alt="ISO27017" className="h-12" />
            <img src="https://www.kaonavi.jp/img/common/img_privacy.png" alt="プライバシーマーク" className="h-12" />
          </div>

          <p className="text-sm text-gray-500 mt-8">© 2012-2026 kaonavi, inc.</p>
          <p className="text-xs text-gray-400 mt-2">
            サイトに掲載されているカオナビ製品画面内の顔写真・プロフィール等はサンプルです。
          </p>
        </div>
      </footer>
    </div>
  );
}
